import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArmadaNav } from "@/components/ArmadaNav";
import { RankBadge, RoleBadge } from "@/components/Badges";
import GoldenPassBadge from "@/components/GoldenPassBadge";
import { Coins, Vault, Activity, Sparkles, Crown, Star, TrendingUp, Brain, GraduationCap, Shield } from "lucide-react";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

function StatCard({ label, value, sub, icon: Icon, accent = "#D4AF37", testId }) {
  return (
    <div
      data-testid={testId}
      className="glass-strong rounded-xl p-6 border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-300"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-armada font-bold" style={{ color: accent }}>
          {label}
        </div>
        {Icon && (
          <div
            className="w-9 h-9 rounded-md flex items-center justify-center"
            style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}
          >
            <Icon className="w-4 h-4" style={{ color: accent }} />
          </div>
        )}
      </div>
      <div className="text-3xl md:text-4xl font-black text-white tracking-tighter">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2">{sub}</div>}
    </div>
  );
}

export default function JuniorCommandPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/heir-vault/me");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const isMatriarch = data?.heir?.theme === "island_gold";
  const headerLabel = isMatriarch
    ? "ANCESTRAL MATRIARCH · ISLAND GOLD"
    : "JUNIOR COMMAND DASHBOARD";

  return (
    <div className={`min-h-screen ${isMatriarch ? "bg-[#1a0f00]" : "bg-armada-base"} relative`}>
      {isMatriarch && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, rgba(255,180,80,0.18), transparent 55%), radial-gradient(ellipse at bottom right, rgba(180,140,60,0.12), transparent 60%)",
          }}
        />
      )}
      <div className="absolute inset-0 dashboard-grid opacity-20 pointer-events-none" />
      <div className="relative">
        <ArmadaNav />
        <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
          <div className="mb-8 animate-fade-up">
            <div
              className={`text-[10px] tracking-armada font-bold mb-2 ${
                isMatriarch ? "text-amber-300" : "text-[#D4AF37]"
              }`}
            >
              {headerLabel}
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
              {isMatriarch ? "Welcome, Matriarch " : "Welcome, "}
              <span className={isMatriarch ? "text-amber-300" : "gold-shimmer"}>
                {data?.heir?.full_name?.split(" ")[0] || user?.full_name?.split(" ")[0] || "Heir"}
              </span>
            </h1>
            <div className="flex items-center gap-3 mt-3 text-zinc-400 flex-wrap">
              <RoleBadge role={user?.role} />
              <RankBadge rank={user?.rank} size="lg" />
              {data?.heir?.matriarch_title && (
                <span
                  data-testid="matriarch-title-badge"
                  className="text-[10px] tracking-armada font-bold px-2.5 py-1 rounded-md border border-amber-400/40 bg-amber-400/10 text-amber-200"
                >
                  {data.heir.matriarch_title.toUpperCase()}
                </span>
              )}
              {data?.heir?.mother && (
                <span className="text-xs tracking-wider text-zinc-500">
                  · Lineage · <span className="text-rose-300">{data.heir.mother}</span>
                </span>
              )}
            </div>
          </div>

          {err && (
            <div
              data-testid="junior-error"
              className="mb-6 p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm"
            >
              {err}
            </div>
          )}

          {data && (
            <>
              <GoldenPassBadge variant="card" />
              {/* Heir Vault hero */}
              <div
                data-testid="heir-vault-hero"
                className="glass-strong rounded-2xl p-8 mb-8 border border-[#D4AF37]/20 relative overflow-hidden"
              >
                <div
                  className="absolute inset-0 opacity-10 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle at 80% 20%, rgba(212,175,55,0.4), transparent 60%)",
                  }}
                />
                <div className="relative flex items-start justify-between flex-wrap gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Vault className="w-5 h-5 text-[#D4AF37]" />
                      <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold">
                        SOVEREIGN HEIR VAULT
                      </div>
                    </div>
                    <div className="text-sm text-zinc-400 max-w-md">
                      Your personal dynasty pool. Routed automatically from every Stripe checkout
                      across the Armada — no human approval, no delay.
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-armada text-zinc-500 mb-1">SHARE</div>
                    <div className="text-2xl font-black text-white">
                      {data.vault.ancestral_share_pct?.toFixed?.(4)}%
                      <span className="text-zinc-500 text-sm font-medium"> Anc</span>
                      <span className="mx-2 text-zinc-700">·</span>
                      {data.vault.live_share_pct?.toFixed?.(4)}%
                      <span className="text-zinc-500 text-sm font-medium"> Live</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
                  <div
                    data-testid="vault-ancestral"
                    className="p-6 rounded-xl border border-amber-500/20 bg-amber-500/5"
                  >
                    <div className="flex items-center gap-2 text-[10px] tracking-armada text-amber-300 font-bold mb-3">
                      <Crown className="w-4 h-4" /> ANCESTRAL POOL · LONG-TERM
                    </div>
                    <div className="text-5xl font-black text-white tracking-tighter">
                      {fmtUSD(data.vault.ancestral_balance_usd)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-2">
                      Locked dynasty capital · 5% of every armada checkout, divided across heirs
                    </div>
                  </div>
                  <div
                    data-testid="vault-live"
                    className="p-6 rounded-xl border border-emerald-500/20 bg-emerald-500/5"
                  >
                    <div className="flex items-center gap-2 text-[10px] tracking-armada text-emerald-300 font-bold mb-3">
                      <Sparkles className="w-4 h-4" /> LIVE HEIR TAX · SPENDABLE
                    </div>
                    <div className="text-5xl font-black text-white tracking-tighter">
                      {fmtUSD(data.vault.live_balance_usd)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-2">
                      2% live-ops tax · Available for your missions, sponsored by the Armada
                    </div>
                  </div>
                </div>
              </div>

              {/* Multiplier badges (CyberKids + Sovereign Shield) */}
              {(data.multipliers?.cyberkids?.active || data.multipliers?.sovereign_shield?.verified) && (
                <div
                  data-testid="multiplier-badges"
                  className="glass-strong rounded-xl p-6 mb-8 border border-cyan-400/20 relative overflow-hidden"
                >
                  <div
                    className="absolute inset-0 opacity-10 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(circle at 50% 50%, rgba(34,211,238,0.4), transparent 60%)",
                    }}
                  />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield className="w-4 h-4 text-cyan-300" />
                      <div className="text-[10px] tracking-armada text-cyan-300 font-bold">
                        TREASURY MULTIPLIERS · CUMULATIVE WITH BASE VAULT
                      </div>
                      <span className="ml-2 text-[10px] tracking-wider text-cyan-200 px-2 py-0.5 rounded-md border border-cyan-400/40 bg-cyan-400/10">
                        +{data.multipliers.combined_treasury_pct}% PER CHECKOUT
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        data-testid="multiplier-cyberkids"
                        className={`p-5 rounded-xl border ${
                          data.multipliers.cyberkids.active
                            ? "border-cyan-400/40 bg-cyan-400/5"
                            : "border-zinc-700/40 bg-black/20 opacity-60"
                        }`}
                      >
                        <div className="flex items-center gap-2 text-[10px] tracking-armada text-cyan-300 font-bold mb-2">
                          <GraduationCap className="w-4 h-4" /> CYBERKIDS ENROLLMENT
                          <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            data.multipliers.cyberkids.active
                              ? "bg-cyan-400/20 text-cyan-200" : "bg-zinc-700/40 text-zinc-400"
                          }`}>
                            {data.multipliers.cyberkids.status?.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-2xl font-black text-white tracking-tighter">
                          {fmtUSD(data.multipliers.cyberkids.lifetime_usd)}
                          <span className="text-sm font-medium text-zinc-500"> lifetime</span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">
                          +1% of every checkout · sourced from Fleet Treasury
                        </div>
                      </div>
                      <div
                        data-testid="multiplier-shield"
                        className={`p-5 rounded-xl border ${
                          data.multipliers.sovereign_shield.verified
                            ? "border-purple-400/40 bg-purple-400/5"
                            : "border-zinc-700/40 bg-black/20 opacity-60"
                        }`}
                      >
                        <div className="flex items-center gap-2 text-[10px] tracking-armada text-purple-300 font-bold mb-2">
                          <Brain className="w-4 h-4" /> SOVEREIGN SHIELD
                          <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            data.multipliers.sovereign_shield.verified
                              ? "bg-purple-400/20 text-purple-200" : "bg-zinc-700/40 text-zinc-400"
                          }`}>
                            {data.multipliers.sovereign_shield.verified ? "VERIFIED" : "PENDING"}
                          </span>
                        </div>
                        <div className="text-2xl font-black text-white tracking-tighter">
                          {fmtUSD(data.multipliers.sovereign_shield.lifetime_usd)}
                          <span className="text-sm font-medium text-zinc-500"> lifetime</span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {data.multipliers.sovereign_shield.diagnosis_label ? (
                            <>+1% Neurodivergent Multiplier · <span className="text-purple-200">{data.multipliers.sovereign_shield.diagnosis_label}</span> · from Fleet Treasury</>
                          ) : (
                            <>+1% Neurodivergent Multiplier · from Fleet Treasury</>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 24h metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                <StatCard
                  testId="stat-24h-ancestral"
                  label="ANCESTRAL · 24H"
                  value={fmtUSD((data.last_24h?.ancestral?.total_usd) || 0)}
                  sub={`${data.last_24h?.ancestral?.count || 0} credits`}
                  icon={Crown}
                  accent="#F59E0B"
                />
                <StatCard
                  testId="stat-24h-live"
                  label="LIVE · 24H"
                  value={fmtUSD((data.last_24h?.live?.total_usd) || 0)}
                  sub={`${data.last_24h?.live?.count || 0} credits`}
                  icon={Sparkles}
                  accent="#34D399"
                />
                <StatCard
                  testId="stat-status"
                  label="VAULT STATUS"
                  value={data.vault.status || "ACTIVE"}
                  sub={`Initialized ${
                    data.vault.initialized_at
                      ? new Date(data.vault.initialized_at).toLocaleDateString()
                      : "—"
                  }`}
                  icon={Vault}
                />
              </div>

              {/* Recent ledger */}
              <div
                data-testid="vault-ledger"
                className="glass-strong rounded-xl p-6 border border-white/5"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Activity className="w-4 h-4 text-[#D4AF37]" />
                  <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold">
                    PERSONAL LEDGER · RECENT 20 CREDITS
                  </div>
                </div>
                {data.recent_ledger?.length === 0 ? (
                  <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-white/10 rounded-md">
                    No credits yet. Vault activates the moment the next checkout settles.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.recent_ledger.map((row, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 rounded-md bg-black/30 border border-white/5 hover:border-[#D4AF37]/20 transition"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              row.bucket === "ancestral" ? "bg-amber-400" : "bg-emerald-400"
                            }`}
                          />
                          <div>
                            <div className="text-sm text-white font-semibold">
                              {row.bucket === "ancestral" ? "Ancestral Credit" : "Live Heir Credit"}
                            </div>
                            <div className="text-[10px] tracking-wider text-zinc-500">
                              {row.source_channel} ·{" "}
                              {new Date(row.credited_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-bold text-white tabular-nums">
                          +{fmtUSD((row.amount_cents || 0) / 100)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
